import {zodResolver} from '@hookform/resolvers/zod';
import {Button, PasswordInput, TextInput, rem} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {IconArrowLeft} from '@tabler/icons-react';
import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';

import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  PasswordResetState,
  confirmUserPassword,
  resetAuthState,
  resetUserPassword,
} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {z} from 'zod';
import {PasswordStrength} from 'src/design/molecules/PasswordStrength';
import classes from './ResetPassword.module.css';

const schema = z
  .object({
    identifier: z.string(),
    code: z
      .string()
      .length(6, {message: 'Confirmation code should be 6 characters.'}),

    password: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'], // path of error
  })
  .superRefine(({password}, checkPassComplexity) => {
    const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
    const containsLowercase = (ch: string) => /[a-z]/.test(ch);
    const containsSpecialChar = (ch: string) =>
      /[`!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?~ ]/.test(ch);
    let countOfUpperCase = 0,
      countOfLowerCase = 0,
      countOfNumbers = 0,
      countOfSpecialChar = 0;
    for (let i = 0; i < password.length; i++) {
      const ch = password.charAt(i);
      if (!isNaN(+ch)) countOfNumbers++;
      else if (containsUppercase(ch)) countOfUpperCase++;
      else if (containsLowercase(ch)) countOfLowerCase++;
      else if (containsSpecialChar(ch)) countOfSpecialChar++;
    }
    if (
      countOfLowerCase < 1 ||
      countOfUpperCase < 1 ||
      countOfSpecialChar < 1 ||
      countOfNumbers < 1
    ) {
      checkPassComplexity.addIssue({
        code: 'custom',
        message: 'password does not meet complexity requirements',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export const ResetPassword: React.FC = () => {
  const {username, passwordResetStep} = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      identifier: username ?? '',
      code: '',
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    // First thing we do is trigger the reset password flow. Then, allow the user to complete it.
    void dispatch(resetUserPassword(getValues('identifier')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(values: FormValues): void {
    void dispatch(
      confirmUserPassword({
        username: values.identifier,
        confirmationCode: values.code,
        newPassword: values.confirmPassword,
      })
    );
  }

  const handleBackToLogin = () => {
    dispatch(resetAuthState());
  };

  return (
    <>
      {passwordResetStep === PasswordResetState.PendingConfirmationCode && (
        <>
          <form onSubmit={handleFormSubmit(handleSubmit)} autoComplete="off">
            <Alert mb="md" color="success">
              {`Your password must be reset. This may be due to an administrator or other security action taken to protect your account. An automated confirmation code was sent to your email by your administrator. Please enter the confirmation code below to reset your password.`}
            </Alert>
            <Text mb="md" size="sm" c="dimmed">
              If you were logging in with a passwordless link, you may use the
              link again to login after resetting your password.
            </Text>
            <Controller
              name="code"
              control={control}
              render={({field, fieldState}) => (
                <TextInput
                  label="Code"
                  placeholder="Code"
                  {...field}
                  error={fieldState.error?.message}
                  autoComplete="new-password"
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({field, fieldState}) => (
                <PasswordInput
                  label="Password"
                  placeholder="Code"
                  {...field}
                  error={fieldState.error?.message}
                  autoComplete="new-password"
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({field, fieldState}) => (
                <PasswordInput
                  label="Confirm Password"
                  placeholder="Code"
                  {...field}
                  error={fieldState.error?.message}
                  autoComplete="new-password"
                />
              )}
            />

            <PasswordStrength password={watch('password')} />

            <Group justify="space-between" mt="lg" className={classes.controls}>
              <Anchor
                c="dimmed"
                size="sm"
                className={classes.control}
                component={Link}
                to={PathUtility.Login()}
                onClick={handleBackToLogin}>
                <Center inline>
                  <IconArrowLeft
                    style={{width: rem(12), height: rem(12)}}
                    stroke={1.5}
                  />
                  <Box ml={5}>Back to the login page</Box>
                </Center>
              </Anchor>
              <Button type="submit">Confirm</Button>
            </Group>
          </form>
        </>
      )}
      {passwordResetStep === PasswordResetState.Done && (
        <>
          <Text mb="md">
            Your password has been reset! You may now return to the Login screen
            and Login with your new password.
          </Text>
          <Text mb="md">
            If you were logging in with a passwordless link, you may use the
            link again to login.
          </Text>
          <Button
            fullWidth
            component={Link}
            to={PathUtility.Login()}
            onClick={handleBackToLogin}
            aria-label="back to login button">
            Back to login
          </Button>
        </>
      )}
    </>
  );
};
